import React, { useEffect, useMemo, useState } from "react";
import { Typography, Menu, Flex, Empty } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { ConnectedProps, connect } from "react-redux";
import { ReducerState, UserOrganization } from "../../types";
import { getUserOrganizations } from "../../actions";
import { useWebServer } from "../../providers";
import { ALL_ORGS, readSuperUser } from "../../utils/activeOrg";
import TrapStatus from "../../components/TrapStatus";
import AlertTable from "../../components/AlertTable";
import UsersTable from "./UsersTable";

const { Title, Text } = Typography;

type Group = "devices" | "users" | "alerts";

/** Sentinel for the bucket that is not an organization. */
const UNAFFILIATED = "__unaffiliated__";

const mapStateToProps = (state: ReducerState) => ({
  userOrganizations: state.userOrganizations,
});

const connector = connect(mapStateToProps, { getUserOrganizations });
type Props = ConnectedProps<typeof connector>;

/**
 * Superuser panel: organizations, and for each of them devices, users and
 * alerts.
 *
 * The device and alert views are the SAME components the rest of the app uses,
 * handed an `org`. That org travels as a per-request header rather than by
 * changing what the operator is acting as, so looking at another tenant here
 * leaves their own context alone - and because the org is part of each query's
 * cache key, the two never share cached data.
 *
 * Access is enforced by the backend on every endpoint; the guard here only
 * avoids rendering a surface that would answer 403 to everything.
 */
const SuperUserPanel: React.FC<Props> = ({
  userOrganizations,
  getUserOrganizations,
}) => {
  const { user } = useAuth0();
  const { sendRequest } = useWebServer();
  const [selected, setSelected] = useState<string | null>(null);
  const [group, setGroup] = useState<Group>("devices");

  const superUser = readSuperUser(user);

  useEffect(() => {
    if (superUser) getUserOrganizations(sendRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superUser]);

  const orgs = useMemo(
    () =>
      [...userOrganizations].sort((a, b) =>
        (a.display_name || a.uuid).localeCompare(b.display_name || b.uuid),
      ),
    [userOrganizations],
  );

  const orgNames = useMemo(
    () => new Map(orgs.map((o) => [o.uuid, o.display_name || o.uuid])),
    [orgs],
  );

  if (!superUser) {
    return (
      <div className="px-6 py-10">
        <Empty description="Super User access required" />
      </div>
    );
  }

  // The unaffiliated bucket is not an organization, so it is its own entry
  // rather than a row in the list.
  const isUnaffiliated = selected === UNAFFILIATED;
  const isAll = selected === ALL_ORGS;

  return (
    <div className="px-6 h-full w-full flex flex-col">
      <Title level={2} className="my-6">
        Super User
      </Title>

      <Flex gap={24} className="flex-1 min-h-0">
        {/* Neither side may size to its content: the alerts table scrolls
            horizontally inside its own box, but a flex item's default minimum
            width is its content's, so the panel grew to the table's full
            scroll width, crushed the menu and ran off the page. */}
        <div style={{ width: 260, flexShrink: 0, overflowY: "auto" }}>
          <Menu
            mode="inline"
            selectedKeys={selected ? [`${selected}:${group}`] : []}
            items={[
              // Users are left out: the per-organization lists already cover
              // everyone, and UsersTable has no all-organizations view.
              {
                key: ALL_ORGS,
                label: "All",
                children: (["devices", "alerts"] as Group[]).map((g) => ({
                  key: `${ALL_ORGS}:${g}`,
                  label: g[0].toUpperCase() + g.slice(1),
                  onClick: () => {
                    setSelected(ALL_ORGS);
                    setGroup(g);
                  },
                })),
              },
              ...orgs.map((org: UserOrganization) => ({
                key: org.uuid,
                label: org.display_name || org.uuid,
                children: (["devices", "users", "alerts"] as Group[]).map(
                  (g) => ({
                    key: `${org.uuid}:${g}`,
                    label: g[0].toUpperCase() + g.slice(1),
                    onClick: () => {
                      setSelected(org.uuid);
                      setGroup(g);
                    },
                  }),
                ),
              })),
              {
                key: UNAFFILIATED,
                label: "Unaffiliated",
                children: [
                  {
                    key: `${UNAFFILIATED}:users`,
                    label: "Users",
                    onClick: () => {
                      setSelected(UNAFFILIATED);
                      setGroup("users");
                    },
                  },
                ],
              },
            ]}
          />
        </div>

        {/* Keyed by organization so nothing carries over from the previous
            one: a still-mounted query hook hands back its old result while the
            new org's loads, which would send every org's devices under this
            org's header, and the table's filters would name the old org's. */}
        <div
          key={selected ?? ""}
          className="flex-1 min-h-0 min-w-0 flex flex-col"
        >
          {!selected ? (
            <Empty description="Select an organization" className="mt-16" />
          ) : isAll ? (
            <>
              <Title level={4}>All organizations</Title>
              {group === "alerts" && (
                <AlertTable org={ALL_ORGS} orgNames={orgNames} />
              )}
              {group === "devices" && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <TrapStatus org={ALL_ORGS} orgNames={orgNames} />
                </div>
              )}
            </>
          ) : isUnaffiliated ? (
            <>
              <Title level={4}>Unaffiliated users</Title>
              <Text type="secondary" className="block mb-4">
                No Scout access
              </Text>
              <div className="flex-1 min-h-0 overflow-auto">
                <UsersTable organizations={orgs} />
              </div>
            </>
          ) : (
            <>
              <Title level={4}>
                {orgs.find((o) => o.uuid === selected)?.display_name ||
                  selected}
              </Title>
              {/* Alerts gets the full remaining height rather than sitting in a
                  scroller: AlertTable is itself a flex column that grows and
                  scrolls its own body, which is how the standalone
                  Alerts & Notifications page renders it. */}
              {group === "alerts" && <AlertTable org={selected} />}
              {group === "devices" && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <TrapStatus org={selected} />
                </div>
              )}
              {group === "users" && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <UsersTable org={selected} organizations={orgs} />
                </div>
              )}
            </>
          )}
        </div>
      </Flex>
    </div>
  );
};

export default connector(SuperUserPanel);
